import React, { useState, useEffect } from 'react';
import {
  Card,
  Typography,
  CircularProgress,
  Alert,
  Box,
  LinearProgress,
  Chip
} from '@mui/material';
import { 
  TrendingUp, 
  TrendingDown, 
  Speed,
  CheckCircle,
  Warning,
  Error as ErrorIcon
} from '@mui/icons-material';
import { analyticsApi } from '../../services/api';

interface SpendingVelocityData {
  currentMonthSpent: number;
  averageMonthlySpending: number;
  daysIntoMonth: number;
  totalDaysInMonth: number;
  projectedMonthlySpending: number;
  spendingVelocity: number; // percentage of how fast we're spending compared to average
  status: 'on_track' | 'ahead' | 'behind';
}

const SpendingVelocityChart: React.FC = () => {
  const [data, setData] = useState<SpendingVelocityData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Get current month cash flow and last 12 months data
        const [currentYearData, cashFlowData] = await Promise.all([
          analyticsApi.getMonthOverMonth(new Date().getFullYear()),
          analyticsApi.getMonthlyCashFlow(12)
        ]);

        const now = new Date();
        const currentMonth = now.getMonth() + 1; // 1-based
        const currentDay = now.getDate();
        const totalDaysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();

        // Get current month spending from current year data
        const currentMonthData = currentYearData.current_year.find(
          (month: any) => month.month === new Date(0, currentMonth - 1).toLocaleString('default', { month: 'long' })
        );
        const currentMonthSpent = currentMonthData?.amount || 0;

        // Calculate average monthly spending from last 12 months
        const totalSpending = cashFlowData.data.reduce((sum: number, month: any) => sum + month.expenses, 0);
        const averageMonthlySpending = totalSpending / Math.max(cashFlowData.data.length, 1);

        // Calculate velocity and projections
        const expectedSpentByNow = (currentDay / totalDaysInMonth) * averageMonthlySpending;
        const projectedMonthlySpending = (currentMonthSpent / currentDay) * totalDaysInMonth;
        const spendingVelocity = expectedSpentByNow > 0 ? (currentMonthSpent / expectedSpentByNow) * 100 : 0;

        let status: 'on_track' | 'ahead' | 'behind' = 'on_track';
        if (spendingVelocity > 120) status = 'ahead';
        else if (spendingVelocity < 80) status = 'behind';

        setData({
          currentMonthSpent,
          averageMonthlySpending,
          daysIntoMonth: currentDay,
          totalDaysInMonth,
          projectedMonthlySpending,
          spendingVelocity,
          status
        });
        
        setError(null);
      } catch (err) {
        setError('Failed to load spending velocity data');
        console.error('Error fetching spending velocity:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-CH', {
      style: 'currency',
      currency: 'CHF',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'on_track': return 'success';
      case 'ahead': return 'error';
      case 'behind': return 'warning';
      default: return 'primary';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'on_track': return <CheckCircle />;
      case 'ahead': return <ErrorIcon />;
      case 'behind': return <Warning />;
      default: return <Speed />;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'on_track': return 'On Track';
      case 'ahead': return 'Spending Fast';
      case 'behind': return 'Under Budget';
      default: return 'Unknown';
    }
  };

  if (loading) {
    return (
      <Card sx={{ p: { xs: 2, md: 3 }, height: { xs: 350, sm: 400 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
          <CircularProgress />
        </Box>
      </Card>
    );
  }

  if (error) {
    return (
      <Card sx={{ p: { xs: 2, md: 3 } }}>
        <Alert severity="error">{error}</Alert>
      </Card>
    );
  }

  if (!data) {
    return (
      <Card sx={{ p: { xs: 2, md: 3 } }}>
        <Alert severity="info">No spending velocity data available</Alert>
      </Card>
    );
  }

  const progressPercentage = Math.min((data.daysIntoMonth / data.totalDaysInMonth) * 100, 100);
  const spendingPercentage = Math.min((data.currentMonthSpent / data.averageMonthlySpending) * 100, 150);

  return (
    <Card sx={{ p: { xs: 2, md: 3 }, height: { xs: 350, sm: 400 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Speed sx={{ mr: 1, color: 'primary.main' }} />
        <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.main', fontSize: { xs: '1.1rem', md: '1.25rem' } }}>
          Monthly Spending Velocity
        </Typography>
      </Box>

      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Month Progress
          </Typography>
          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
            Day {data.daysIntoMonth} of {data.totalDaysInMonth}
          </Typography>
        </Box>
        <LinearProgress 
          variant="determinate" 
          value={progressPercentage} 
          sx={{ height: 8, borderRadius: 4, mb: 1 }}
        />
        <Typography variant="caption" color="text.secondary">
          {Math.round(progressPercentage)}% through the month
        </Typography>
      </Box>

      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Spending vs Average
          </Typography>
          <Chip 
            icon={getStatusIcon(data.status)}
            label={getStatusText(data.status)}
            color={getStatusColor(data.status) as any}
            size="small"
            variant="outlined"
          />
        </Box>
        <LinearProgress 
          variant="determinate" 
          value={spendingPercentage} 
          sx={{ 
            height: 8, 
            borderRadius: 4, 
            mb: 1,
            '& .MuiLinearProgress-bar': {
              backgroundColor: data.status === 'ahead' ? 'error.main' : 
                              data.status === 'behind' ? 'warning.main' : 'success.main'
            }
          }}
        />
        <Typography variant="caption" color="text.secondary">
          {Math.round(data.spendingVelocity)}% of expected pace
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ flex: 1, minWidth: { xs: 'auto', sm: 120 } }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Spent This Month
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 'bold', fontFamily: 'monospace' }}>
            {formatCurrency(data.currentMonthSpent)}
          </Typography>
        </Box>
        
        <Box sx={{ flex: 1, minWidth: { xs: 'auto', sm: 120 } }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Monthly Average
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 'bold', fontFamily: 'monospace' }}>
            {formatCurrency(data.averageMonthlySpending)}
          </Typography>
        </Box>
        
        <Box sx={{ flex: 1, minWidth: { xs: 'auto', sm: 120 } }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Projected Total
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 'bold', 
                fontFamily: 'monospace',
                color: data.projectedMonthlySpending > data.averageMonthlySpending ? 'error.main' : 'success.main'
              }}
            >
              {formatCurrency(data.projectedMonthlySpending)}
            </Typography>
            {data.projectedMonthlySpending > data.averageMonthlySpending ? 
              <TrendingUp sx={{ ml: 0.5, color: 'error.main', fontSize: 16 }} /> :
              <TrendingDown sx={{ ml: 0.5, color: 'success.main', fontSize: 16 }} />
            }
          </Box>
        </Box>
      </Box>
    </Card>
  );
};

export default SpendingVelocityChart;